import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { catchError, defer, finalize, map, Observable, of } from 'rxjs';
import { StatisticsApiService } from 'src/app/core/api/dashboard/statistics-api.service';
import { AlertService } from 'src/app/shared/services/alert/alert.service';
import { ERROR_LIST } from 'src/app/core/constants/error-handling-list/error-list.enum';
import { ErrorCode } from 'src/app/core/constants/error-handling-list/error-code';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import {
  EmployeeStatisticCardParams,
  EmployeeStatisticCardPreloadService,
} from 'src/app/core/services/employee-statistic-card-preload.service';

@Injectable({
  providedIn: 'root',
})
export class EmployeeStatisticProfileGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly statisticsApiService = inject(StatisticsApiService);
  private readonly alertService = inject(AlertService);
  private readonly globalSpinnerService = inject(GlobalSpinnerService);
  private readonly cardPreloadService = inject(EmployeeStatisticCardPreloadService);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | UrlTree {
    const userId = route.queryParamMap.get('userId');

    if (!userId) return this.router.parseUrl('/dashboard');

    const params: EmployeeStatisticCardParams = { filters: [], date_time_range: null };

    return defer(() => {
      this.globalSpinnerService.setIsLoading(true);
      this.cardPreloadService.clear();

      return this.statisticsApiService.getRoundeeCard(userId, params, true);
    }).pipe(
      map((report): boolean | UrlTree => {
        this.cardPreloadService.set(userId, params, report);

        return true;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error?.error?.code === ErrorCode.ReportArchivedEmployeeAccessDenied) {
          this.alertService.addErrorAlert(ERROR_LIST.REPORT_ARCHIVED_EMPLOYEE_ACCESS_DENIED);
          return of(this.router.parseUrl('/dashboard'));
        }
        return of(true);
      }),
      finalize(() => this.globalSpinnerService.setIsLoading(false))
    );
  }
}
